import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Server, AlertTriangle, Activity, Clock, Network, CheckCircle2, AlertCircle, History, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SEV_COLORS = ['bg-gray-200','bg-blue-100','bg-yellow-100','bg-orange-100','bg-red-100','bg-red-600'];
const SEV_LABELS = ['N/C','Info','Warning','Average','High','Disaster'];

export function HostDetailPage() {
  const { hostId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(API + '/hosts/' + hostId, { headers: getAuthHeader() });
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar detalhes do host');
    } finally {
      setLoading(false);
    }
  }, [hostId, getAuthHeader]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-4 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
        <p className="text-lg text-gray-600">{error}</p>
        <Button variant="outline" onClick={() => navigate('/dashboard/hosts')} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />Voltar para Hosts
        </Button>
      </div>
    );
  }

  const host = data?.host;
  const problems = data?.problems || [];
  const triggers = data?.triggers || [];
  const timeline = data?.timeline || [];
  const items = data?.items || [];
  const hasProblems = problems.length > 0;
  const maxSeverity = hasProblems ? Math.max(...problems.map(p => p.severity)) : 0;

  const getStatusBadge = () => {
    if (!hasProblems) return <Badge className="bg-green-100 text-green-700">OK</Badge>;
    if (maxSeverity >= 5) return <Badge className="bg-red-600 text-white animate-pulse">DISASTER</Badge>;
    if (maxSeverity >= 4) return <Badge className="bg-red-500 text-white">HIGH</Badge>;
    if (maxSeverity >= 3) return <Badge className="bg-orange-500 text-white">AVERAGE</Badge>;
    return <Badge className="bg-yellow-500 text-white">WARNING</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="host-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/hosts')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{host?.name}</h1>
            {getStatusBadge()}
          </div>
          <p className="text-sm text-muted-foreground">{host?.host}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="text-2xl font-bold mt-1">{hasProblems ? 'Com Problemas' : 'OK'}</p>
              </div>
              <div className={hasProblems ? 'h-12 w-12 rounded-xl flex items-center justify-center bg-red-50 text-red-700' : 'h-12 w-12 rounded-xl flex items-center justify-center bg-green-50 text-green-700'}>
                {hasProblems ? <AlertTriangle className="h-6 w-6" /> : <CheckCircle2 className="h-6 w-6" />}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Problemas Ativos</p>
                <p className="text-2xl font-bold mt-1">{problems.length}</p>
              </div>
              <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-gray-50 text-gray-700">
                <AlertCircle className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Triggers</p>
                <p className="text-2xl font-bold mt-1">{triggers.filter(t => t.value === '1').length} / {triggers.length}</p>
              </div>
              <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-gray-50 text-gray-700">
                <Activity className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Interfaces</p>
                <p className="text-2xl font-bold mt-1">{host?.interfaces?.length || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-gray-50 text-gray-700">
                <Network className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="problems">Problemas ({problems.length})</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="items">Métricas ({items.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Info className="h-4 w-4" />Informações do Host
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between py-2 border-b"><span className="text-muted-foreground">Nome</span><span className="font-medium">{host?.name}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-muted-foreground">Host</span><span className="font-medium">{host?.host}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-muted-foreground">Status</span><span className="font-medium">{host?.status === 'enabled' ? 'Habilitado' : 'Desabilitado'}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-muted-foreground">Grupos</span><span className="font-medium">{host?.groups?.join(', ') || '-'}</span></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Network className="h-4 w-4" />Interfaces de Rede
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!host?.interfaces?.length ? (
                  <p className="text-muted-foreground text-center py-4">Nenhuma interface</p>
                ) : (
                  <div className="space-y-3">
                    {host.interfaces.map((iface, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm">{iface.ip || iface.dns}</span>
                          <Badge variant="secondary">{iface.type === '1' ? 'Agent' : iface.type === '2' ? 'SNMP' : 'Outro'}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Porta: {iface.port}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" />Triggers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Prioridade</TableHead>
                      <TableHead>Última Mudança</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {triggers.map(trigger => (
                      <TableRow key={trigger.id}>
                        <TableCell>
                          {trigger.value === '1' ? (
                            <Badge className="bg-red-100 text-red-700">PROBLEM</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">OK</Badge>
                          )}
                        </TableCell>
                        <TableCell className="max-w-md truncate">{trigger.description}</TableCell>
                        <TableCell>
                          <Badge className={SEV_COLORS[trigger.priority] || 'bg-gray-200'}>
                            {SEV_LABELS[trigger.priority] || 'N/C'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {trigger.lastchange > 0 ? new Date(trigger.lastchange * 1000).toLocaleString('pt-BR') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="problems">
          <Card>
            <CardContent className="p-0">
              {problems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
                  <p className="text-lg text-gray-600">Nenhum problema ativo</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Severidade</TableHead>
                      <TableHead>Problema</TableHead>
                      <TableHead>Início</TableHead>
                      <TableHead>Reconhecido</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {problems.map(problem => (
                      <TableRow key={problem.id}>
                        <TableCell>
                          <Badge className={SEV_COLORS[problem.severity]}>{SEV_LABELS[problem.severity]}</Badge>
                        </TableCell>
                        <TableCell>{problem.name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(problem.clock * 1000).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          {problem.acknowledged ? (
                            <Badge className="bg-green-100 text-green-700">Sim</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Não</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4" />Histórico de Eventos (últimos 7 dias)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
                  <div className="space-y-4">
                    {timeline.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">Nenhum evento recente</p>
                    ) : (
                      timeline.map((event, idx) => (
                        <div key={event.id || idx} className="relative pl-10">
                          <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-background ${event.severity >= 4 ? 'bg-red-500' : event.severity >= 3 ? 'bg-orange-500' : event.severity >= 2 ? 'bg-yellow-500' : 'bg-gray-400'}`} />
                          <div className="p-3 rounded-lg bg-muted/50">
                            <div className="flex items-center justify-between">
                              <Badge className={SEV_COLORS[event.severity] || 'bg-gray-200'}>{SEV_LABELS[event.severity] || 'Info'}</Badge>
                              <span className="text-xs text-muted-foreground">{event.datetime}</span>
                            </div>
                            <p className="mt-2 text-sm">{event.name}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="items">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Métrica</TableHead>
                    <TableHead>Chave</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Última Coleta</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{item.key}</TableCell>
                      <TableCell>{item.value} {item.units}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.lastclock > 0 ? new Date(item.lastclock * 1000).toLocaleString('pt-BR') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
